import { Point2dUtils } from '../Point2dUtils';
import { Line2d } from '../Line2d';
import { LineSeg2d } from '../LineSeg2d';
import { ArcSeg2d } from '../arcSeg2d';
import { ERelationOfPointOnSeg, ERelationToSeg, IShape2d, IShapeSeg2d } from '@/editor/types/shape';
import { pointOnSeg } from './other';
import { intersectCommonShape } from './intesection';
import { IPoint2d } from '../IPoint2d';
import { arcThresholdValue, thresholdValue } from '../const';

export function pointSegRelation(
  seg: IShapeSeg2d,
  point: IPoint2d,
  tValue: number = thresholdValue
): ERelationOfPointOnSeg {
  if (Point2dUtils.equals(seg.start, point, tValue)) return ERelationOfPointOnSeg.Start;
  if (Point2dUtils.equals(seg.end, point, tValue)) return ERelationOfPointOnSeg.End;
  if (pointOnSeg(seg, point)) return ERelationOfPointOnSeg.Between;

  return ERelationOfPointOnSeg.NoRelation;
}

// 判断两线段在交点处是否为相切（仅相切，不穿过）。仅当其中一方为圆弧时才可能相切
function isTangencyRelation(mainSeg: IShapeSeg2d, otherSeg: IShapeSeg2d, tolerance: number): boolean {
  const arcSeg = mainSeg instanceof ArcSeg2d ? mainSeg : otherSeg instanceof ArcSeg2d ? otherSeg : null;
  if (!arcSeg) return false;

  const counterSeg = arcSeg === mainSeg ? otherSeg : mainSeg;

  // 弧与弧：两圆相切（外切或内切）
  if (counterSeg instanceof ArcSeg2d) {
    return arcSeg.isTangentTo(counterSeg);
  }

  // 弧与线段：圆心到线段所在直线的距离约等于半径即为相切
  const line = new Line2d({ points: { p1: counterSeg.start, p2: counterSeg.end } });
  return Math.abs(line.pointToLineDistance(arcSeg.center) - arcSeg.radius) <= tolerance;
}

export function segRelations(mainSeg: IShapeSeg2d, otherSeg: IShapeSeg2d): ERelationToSeg | ERelationToSeg[] {
  if (shapeEquals(mainSeg, otherSeg)) {
    return ERelationToSeg.Same;
  } else if (shapeOpposite(mainSeg, otherSeg)) {
    return ERelationToSeg.Opposite;
  }

  if (mainSeg instanceof LineSeg2d && otherSeg instanceof LineSeg2d) {
    // 线段与线段关系
    if (mainSeg.isCollinear(otherSeg) && mainSeg.getOverlapSegment(otherSeg)) {
      return ERelationToSeg.Overlap;
    } else if (Point2dUtils.equals(mainSeg.start, otherSeg.start)) {
      return ERelationToSeg.MainStartStartOverlap;
    } else if (Point2dUtils.equals(mainSeg.start, otherSeg.end)) {
      return ERelationToSeg.MainStartEndOverlap;
    } else if (Point2dUtils.equals(mainSeg.end, otherSeg.start)) {
      return ERelationToSeg.MainEndStartOverlap;
    } else if (Point2dUtils.equals(mainSeg.end, otherSeg.end)) {
      return ERelationToSeg.MainEndEndOverlap;
    } else if (pointOnSeg(otherSeg, mainSeg.start)) {
      return ERelationToSeg.MainStartOnSegInternal;
    } else if (pointOnSeg(otherSeg, mainSeg.end)) {
      return ERelationToSeg.MainEndOnSegInternal;
    } else if (pointOnSeg(mainSeg, otherSeg.start)) {
      return ERelationToSeg.MainSegContainStartInternal;
    } else if (pointOnSeg(mainSeg, otherSeg.end)) {
      return ERelationToSeg.MainSegContainEndInternal;
    } else if (mainSeg.intersection(otherSeg)) {
      return ERelationToSeg.IntersectInternal;
    } else {
      return ERelationToSeg.NoRelation;
    }
  }

  //  ================ 非直线段与直线段相交情况 ================
  const relations = [];
  const relationTolerance =
    otherSeg instanceof ArcSeg2d || mainSeg instanceof ArcSeg2d ? arcThresholdValue : thresholdValue;

  // 主线段起点相交情况
  const mainStartRelation = pointSegRelation(otherSeg, mainSeg.start, relationTolerance);
  switch (mainStartRelation) {
    case ERelationOfPointOnSeg.Start:
      relations.push(ERelationToSeg.MainStartStartOverlap);
      break;
    case ERelationOfPointOnSeg.End:
      relations.push(ERelationToSeg.MainStartEndOverlap);
      break;
    case ERelationOfPointOnSeg.Between:
      relations.push(ERelationToSeg.MainStartOnSegInternal);
      break;
  }

  // 主线段终点相交情况
  const mainEndRelation = pointSegRelation(otherSeg, mainSeg.end, relationTolerance);
  switch (mainEndRelation) {
    case ERelationOfPointOnSeg.Start:
      relations.push(ERelationToSeg.MainEndStartOverlap);
      break;
    case ERelationOfPointOnSeg.End:
      relations.push(ERelationToSeg.MainEndEndOverlap);
      break;
    case ERelationOfPointOnSeg.Between:
      relations.push(ERelationToSeg.MainEndOnSegInternal);
      break;
  }

  // 辅线段起点相交情况
  const otherStartRelation = pointSegRelation(mainSeg, otherSeg.start, relationTolerance);
  if (otherStartRelation === ERelationOfPointOnSeg.Between) {
    relations.push(ERelationToSeg.MainSegContainStartInternal);
  }

  // 辅线段终点相交情况
  const otherEndRelation = pointSegRelation(mainSeg, otherSeg.end, relationTolerance);
  if (otherEndRelation === ERelationOfPointOnSeg.Between) {
    relations.push(ERelationToSeg.MainSegContainEndInternal);
  }

  // 将重叠排除出相交情况
  if (mainSeg instanceof ArcSeg2d && otherSeg instanceof ArcSeg2d && mainSeg.isOverlap(otherSeg)) {
    relations.push(ERelationToSeg.Overlap);
  } else {
    const ips = intersectCommonShape(mainSeg, otherSeg);
    const hasInternalPoint =
      ips &&
      ips.some(
        (p) =>
          !Point2dUtils.equals(p, mainSeg.start, relationTolerance) &&
          !Point2dUtils.equals(p, mainSeg.end, relationTolerance) &&
          !Point2dUtils.equals(p, otherSeg.start, relationTolerance) &&
          !Point2dUtils.equals(p, otherSeg.end, relationTolerance)
      );
    if (hasInternalPoint) {
      if (isTangencyRelation(mainSeg, otherSeg, relationTolerance)) {
        relations.push(ERelationToSeg.Tangency);
      } else {
        relations.push(ERelationToSeg.IntersectInternal);
      }
    }
  }

  if (relations.length === 0) return ERelationToSeg.NoRelation;
  if (relations.length === 1) return relations[0];

  return relations;
}

// 判断两个形状是否相等
export function shapeEquals(shape1: IShape2d, shape2: IShape2d): boolean {
  if (shape1 instanceof LineSeg2d && shape2 instanceof LineSeg2d) {
    return Point2dUtils.equals(shape1.start, shape2.start) && Point2dUtils.equals(shape1.end, shape2.end);
  } else if (shape1 instanceof ArcSeg2d && shape2 instanceof ArcSeg2d) {
    return (
      Point2dUtils.equals(shape1.start, shape2.start) &&
      Point2dUtils.equals(shape1.end, shape2.end) &&
      Point2dUtils.equals(shape1.center, shape2.center) &&
      shape1.clockwise === shape2.clockwise
    );
  }

  return false;
}

// 判断两个形状是否相同，方向相反
export function shapeOpposite(shape1: IShape2d, shape2: IShape2d): boolean {
  if (shape1 instanceof LineSeg2d && shape2 instanceof LineSeg2d) {
    return Point2dUtils.equals(shape1.start, shape2.end) && Point2dUtils.equals(shape1.end, shape2.start);
  } else if (shape1 instanceof ArcSeg2d && shape2 instanceof ArcSeg2d) {
    return (
      Point2dUtils.equals(shape1.start, shape2.end) &&
      Point2dUtils.equals(shape1.end, shape2.start) &&
      Point2dUtils.equals(shape1.center, shape2.center) &&
      shape1.clockwise === !shape2.clockwise
    );
  }

  return false;
}
